import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Image,
    Spinner,
    Alert,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const MarketPlacesPage = () => {
    const [marketPlaces, setMarketPlaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editMarketPlace, setEditMarketPlace] = useState(null);

    // Form state matching MarketPlaceController attributes
    const [name, setName] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [logo, setLogo] = useState(null);
    const [preview, setPreview] = useState(null);

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken")
            ? `Bearer ${localStorage.getItem("authToken")}`
            : "",
    });

    const getImageUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return `${process.env.REACT_APP_IMG_URL}/${path}`;
    };

    // Fetch Market Places
    const fetchMarketPlaces = async () => {
        try {
            setLoading(true);
            setError("");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/market-places`,
                { headers: getAuthHeaders() }
            );
            setMarketPlaces(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load marketplaces.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMarketPlaces();
    }, []);

    // Open Modal
    const handleOpenModal = (marketPlace = null) => {
        setErrors({});
        if (marketPlace) {
            setEditMarketPlace(marketPlace);
            setName(marketPlace.name || "");
            setBaseUrl(marketPlace.base_url || "");
            setLogo(null);
            setPreview(marketPlace.logo ? getImageUrl(marketPlace.logo) : null);
        } else {
            setEditMarketPlace(null);
            setName("");
            setBaseUrl("");
            setLogo(null);
            setPreview(null);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditMarketPlace(null);
        setName("");
        setBaseUrl("");
        setLogo(null);
        setPreview(null);
        setErrors({});
    };

    // Handle Logo Change
    const handleLogoChange = (e) => {
        const file = e.target.files?.[0] || null;
        setLogo(file);
        if (file) {
            setPreview(URL.createObjectURL(file));
        }
        setErrors((prev) => ({ ...prev, logo: "" }));
    };

    // Create / Update Market Place
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");
        setError("");

        try {
            const formData = new FormData();
            formData.append("name", name);
            if (baseUrl) {
                formData.append("base_url", baseUrl);
            }

            if (logo) {
                formData.append("logo", logo);
            }

            if (editMarketPlace) {
                // Laravel multipart update requires _method: PUT
                formData.append("_method", "PUT");
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/market-places/${editMarketPlace.id}`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage("Marketplace updated successfully.");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/web-manager/market-places`,
                    formData,
                    { headers: getAuthHeaders() }
                );
                setMessage("Marketplace created successfully.");
            }

            handleCloseModal();
            await fetchMarketPlaces();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Delete Market Place
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this marketplace?")) return;

        try {
            setError("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/web-manager/market-places/${id}`,
                { headers: getAuthHeaders() }
            );
            setMessage("Marketplace deleted successfully.");
            await fetchMarketPlaces();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete marketplace.");
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Market Places Management</h2>
                        <p className="text-muted mb-0">Manage all website marketplaces and logos.</p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            className="px-4 fw-semibold"
                            onClick={() => handleOpenModal()}
                        >
                            + Add New Marketplace
                        </Button>
                    </Col>
                </Row>

                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-2 text-muted">Loading marketplaces...</div>
                            </div>
                        ) : marketPlaces.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No marketplaces found. Click "+ Add New Marketplace" to create one.
                            </div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4">#</th>
                                        <th>Logo</th>
                                        <th>Name</th>
                                        <th>Base URL</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marketPlaces.map((marketPlace, index) => (
                                        <tr key={marketPlace.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td>
                                                {marketPlace.logo ? (
                                                    <Image
                                                        src={getImageUrl(marketPlace.logo)}
                                                        alt={marketPlace.name}
                                                        rounded
                                                        style={{
                                                            width: "50px",
                                                            height: "50px",
                                                            objectFit: "contain",
                                                            backgroundColor: "#f8f9fa",
                                                            padding: "4px",
                                                        }}
                                                    />
                                                ) : (
                                                    <span className="badge bg-secondary">No Logo</span>
                                                )}
                                            </td>
                                            <td className="fw-semibold">{marketPlace.name}</td>
                                            <td>
                                                {marketPlace.base_url ? (
                                                    <a
                                                        href={marketPlace.base_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-none"
                                                    >
                                                        {marketPlace.base_url}
                                                    </a>
                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleOpenModal(marketPlace)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(marketPlace.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* ADD / EDIT MODAL */}
                <Modal
                    show={showModal}
                    onHide={handleCloseModal}
                    centered
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
                        <Modal.Title className="fw-bold fs-4 text-dark">
                            {editMarketPlace ? "Edit Marketplace" : "Add Marketplace"}
                        </Modal.Title>
                    </Modal.Header>

                    <Form onSubmit={handleSubmit}>
                        <Modal.Body className="px-4 py-3">
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Marketplace Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter marketplace name"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setErrors((prev) => ({ ...prev, name: "" }));
                                    }}
                                    isInvalid={!!errors.name}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Base URL
                                </Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://example.com"
                                    value={baseUrl}
                                    onChange={(e) => {
                                        setBaseUrl(e.target.value);
                                        setErrors((prev) => ({ ...prev, base_url: "" }));
                                    }}
                                    isInvalid={!!errors.base_url}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.base_url) ? errors.base_url[0] : errors.base_url}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Marketplace Logo {!editMarketPlace && <span className="text-danger">*</span>}
                                </Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.webp"
                                    onChange={handleLogoChange}
                                    isInvalid={!!errors.logo}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.logo) ? errors.logo[0] : errors.logo}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {preview && (
                                <div className="text-center p-3 bg-light rounded mt-3 border">
                                    <Image
                                        src={preview}
                                        alt="Preview"
                                        style={{ maxHeight: "80px", objectFit: "contain" }}
                                    />
                                </div>
                            )}
                        </Modal.Body>

                        <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end gap-2">
                            <Button
                                variant="secondary"
                                className="px-4 py-2 fw-semibold"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editMarketPlace ? (
                                    "Update Marketplace"
                                ) : (
                                    "Create Marketplace"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default MarketPlacesPage;